import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Person from '@mui/icons-material/Person';
import { Link, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { Field, Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { TextField } from 'formik-mui';
import { object, string } from 'yup';

import LoadingButton from '@/components/LoadingButton';
import {
  phoneVerificationService,
  VerifyResponse,
} from '@/features/persona/services/phoneVerification';
import { SessionTimeoutService } from '@/legacy/services/sessionTimeout';

import { Step } from './Step';

export type EnterVerificationCodeRendererProps = {
  lastPhoneDigits: string;
  onComplete: () => void;
  onResendCode: () => Promise<void>;
  onVerifyCode: (verificationCode: string) => Promise<VerifyResponse>;
};

export const EnterVerificationCodeRenderer = ({
  lastPhoneDigits,
  onComplete,
  onResendCode,
  onVerifyCode,
}: EnterVerificationCodeRendererProps) => {
  const { formatMessage } = useIntl();
  const validationSchema = useMemo(
    () =>
      object({
        verificationCode: string()
          .required(formatMessage({ defaultMessage: 'Please enter a verification code' }))
          .length(6, formatMessage({ defaultMessage: 'Verification code must be 6 digits' })),
      }),
    [formatMessage]
  );
  const onSubmit = useCallback(
    async (
      data: { verificationCode: string },
      { setFieldError }: FormikHelpers<{ verificationCode: string }>
    ): Promise<void> => {
      try {
        const result = await onVerifyCode(data.verificationCode);
        if (!result.valid) {
          setFieldError(
            'verificationCode',
            formatMessage({ defaultMessage: 'The code you have entered is incorrect' })
          );
        } else {
          onComplete();
        }
      } catch (error) {
        if (error.type === 'invalid.authentication') {
          await SessionTimeoutService.showTimeout();
          return;
        }
        const message = error.message
          ? formatMessage(
              {
                defaultMessage: 'Unable to verify code: {errorMessage}',
              },
              { errorMessage: error.message }
            )
          : formatMessage({ defaultMessage: 'Unable to verify code' });
        setFieldError('verificationCode', message);
      }
    },
    [formatMessage, onComplete, onVerifyCode]
  );
  const formik = useFormik({ initialValues: { verificationCode: '' }, onSubmit, validationSchema });
  const { isSubmitting } = formik;
  return (
    <Step>
      <Stack alignItems="center" direction="row">
        <Person fontSize="large" />
        <CheckCircle sx={{ color: 'success.light' }} />
      </Stack>
      <FormikProvider value={formik}>
        <Form>
          <Stack spacing={2}>
            <Field
              autocomplete="one-time-code"
              component={TextField}
              inputMode="numeric"
              label={<FormattedMessage defaultMessage="Verification Code" />}
              name="verificationCode"
              sx={{ maxWidth: 300 }}
              type="text"
            />
            <Typography variant="body2">
              <FormattedMessage
                defaultMessage="Enter the 6-digit code we just sent to ******{lastPhoneDigits}"
                values={{ lastPhoneDigits }}
              />
            </Typography>
            <LoadingButton fullWidth loading={isSubmitting} type="submit">
              <FormattedMessage defaultMessage="Verify and continue" />
            </LoadingButton>
          </Stack>
        </Form>
      </FormikProvider>
      <Stack alignItems="center" direction="row" spacing={1}>
        <Typography variant="body2">
          <FormattedMessage defaultMessage="Didn't get the message?" />
        </Typography>
        {/* This is valid because MUI will render a button element */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link component="button" onClick={onResendCode}>
          <FormattedMessage defaultMessage="Resend code" />
        </Link>
      </Stack>
    </Step>
  );
};

export type EnterVerificationCodeProps = {
  onComplete: () => Promise<void>;
  onResendCode: () => Promise<void>;
  phoneNumber: string;
};

export const EnterVerificationCode = ({
  onComplete,
  onResendCode,
  phoneNumber,
}: EnterVerificationCodeProps) => {
  const onVerifyCode = useCallback(
    async (code: string) => phoneVerificationService.verify(code, phoneNumber),
    [phoneNumber]
  );
  const lastPhoneDigits = useMemo(() => (phoneNumber ? phoneNumber.slice(-4) : ''), [phoneNumber]);
  return (
    <EnterVerificationCodeRenderer
      lastPhoneDigits={lastPhoneDigits}
      onComplete={onComplete}
      onResendCode={onResendCode}
      onVerifyCode={onVerifyCode}
    />
  );
};
